import {
  Box,
  Button,
  Container,
  ContainerProps,
  Grid,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import assessment from "@root/public/images/photos/clarifayo_assessments.png";
import happyWoman from "@root/public/images/photos/happy-woman.png";
import { CalendarIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { PropsWithChildren } from "react";
import { BirdNestIcon } from "../LandingPage/BirdNestIcon";
import { BulletPoint } from "../LandingPage/BulletPoint";
import { DashDivider } from "../LandingPage/DashDivider";
import { CoacheeDoMore } from "./CoacheeDoMore";
import { ScheduleFreeDiscoveryCallButton } from "./ScheduleFreeDiscoveryCallButton";

const SectionContainer = ({
  children,
  ...props
}: PropsWithChildren<ContainerProps>) => {
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 16 }}
      px={{ base: 8, md: 24 }}
      {...props}
    >
      {children}
    </Container>
  );
};

const FirstSection = () => {
  const t = useTranslations("CoacheePage.benefits");
  return (
    <SectionContainer>
      <VStack
        w="full"
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading
          fontSize={"3xl"}
          fontWeight={600}
          dangerouslySetInnerHTML={{ __html: t.raw("title") }}
        />
        <Text
          maxW={"container.lg"}
          fontSize={"1.2rem"}
          textAlign={"center"}
          dangerouslySetInnerHTML={{ __html: t.raw("description") }}
          mb={8}
        />
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={8}
          mb={8}
        >
          <Stack
            direction={"column"}
            gap={{ base: 8, md: 10 }}
            border="2px solid"
            p={8}
            rounded="xl"
            borderColor={"brand.1000"}
          >
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit1"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit2"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit3"),
                }}
              ></Text>
            </BulletPoint>
          </Stack>
          <Stack
            direction={"column"}
            gap={{ base: 8, md: 10 }}
            border="2px solid"
            p={8}
            rounded="xl"
            borderColor={"brand.1000"}
          >
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{ __html: t.raw("benefit4") }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit5"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit6"),
                }}
              ></Text>
            </BulletPoint>
          </Stack>
        </Grid>
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={16}
          mb={8}
        >
          <Box w="full">
            <Image
              src={happyWoman}
              alt="Happy Woman"
              style={{
                objectFit: "cover",
                objectPosition: "center",
                width: "100%",
                height: "100%",
              }}
            />
          </Box>
          <Stack
            direction={"column"}
            gap={{ base: 8, md: 10 }}
            py={8}
            h="full"
            justifyContent={"space-between"}
          >
            <Heading
              fontSize={"2xl"}
              dangerouslySetInnerHTML={{ __html: t.raw("limitedTime") }}
            />
            <ScheduleFreeDiscoveryCallButton label={t("scheduleButton")} />
          </Stack>
        </Grid>
      </VStack>
    </SectionContainer>
  );
};

const SecondSection = () => {
  const t = useTranslations("CoacheePage.benefits");
  return (
    <SectionContainer position="relative" zIndex={1} color="white">
      <VStack
        w="full"
        mt={{
          base: 8,
          md: 16,
        }}
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <DashDivider color="white" />
        <Heading
          fontSize={"3xl"}
          fontWeight={600}
          textAlign={"center"}
          dangerouslySetInnerHTML={{ __html: t.raw("title2") }}
        />
        <Text
          fontSize={"1.2rem"}
          maxW={"container.lg"}
          textAlign={"center"}
          mb={8}
          dangerouslySetInnerHTML={{ __html: t.raw("description2") }}
        />

        <Text
          fontSize={"1.2rem"}
          maxW={"container.lg"}
          textAlign={"center"}
          fontWeight={600}
          dangerouslySetInnerHTML={{ __html: t.raw("results") }}
        />
        <Link href="#schedule">
          <Button
            size="lg"
            fontSize="2xl"
            w={{
              base: "full",
              md: "auto",
            }}
            variant={"primary"}
            leftIcon={<CalendarIcon />}
          >
            {t("scheduleButton")}
          </Button>
        </Link>
        <DashDivider color="white" />
      </VStack>
    </SectionContainer>
  );
};

const ThirdSection = () => {
  const t = useTranslations("CoacheePage.benefits");
  return (
    <SectionContainer>
      <VStack
        w="full"
        mt={{
          base: 8,
          md: 16,
        }}
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading
          fontSize={"3xl"}
          fontWeight={600}
          dangerouslySetInnerHTML={{ __html: t.raw("title3") }}
        />
        <Text
          maxW={"container.lg"}
          fontSize={"1.2rem"}
          textAlign={"center"}
          mb={8}
          dangerouslySetInnerHTML={{ __html: t.raw("description3") }}
        />
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={16}
        >
          <Stack direction={"column"} gap={{ base: 8, md: 10 }} mb={16}>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit11"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit12"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit13"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{ __html: t.raw("benefit14") }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t.raw("benefit15"),
                }}
              ></Text>
            </BulletPoint>
            <BulletPoint>
              <Text
                dangerouslySetInnerHTML={{ __html: t.raw("benefit16") }}
              ></Text>
            </BulletPoint>
          </Stack>

          <Image src={assessment} alt="Happy Woman" />
        </Grid>
        <Text
          fontSize={"1.2rem"}
          maxW={"container.lg"}
          textAlign={"center"}
          fontWeight={600}
          dangerouslySetInnerHTML={{ __html: t.raw("results2") }}
        />
        <DashDivider />
      </VStack>
    </SectionContainer>
  );
};

const FourthSection = () => {
  const t = useTranslations("CoacheePage.benefits");
  return (
    <SectionContainer position="relative" zIndex={1}>
      <VStack
        w="full"
        mt={{
          base: 8,
          md: 16,
        }}
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading
          fontSize={"3xl"}
          fontWeight={600}
          maxW={"container.sm"}
          textAlign={"center"}
          dangerouslySetInnerHTML={{ __html: t.raw("title4") }}
        />
        <Text
          maxW={"container.md"}
          fontSize={"1.2rem"}
          textAlign={"center"}
          mb={8}
          dangerouslySetInnerHTML={{ __html: t.raw("description4") }}
        />

        <DashDivider />
        <BirdNestIcon width={60} height={60} />
        <ScheduleFreeDiscoveryCallButton label={t("scheduleSession")} />
      </VStack>
    </SectionContainer>
  );
};

export const CoacheeBenefits = () => {
  const t = useTranslations("CoacheePage.benefits");
  return (
    <Box id="coachee-benefits">
      <FirstSection />
      <CoacheeDoMore />
      {/* <Box bgColor="brand.1000">
        <SecondSection />
      </Box> */}
      {/* <ThirdSection />*/}

      <FourthSection />
    </Box>
  );
};
